import axios from 'axios';

// Use relative URL if REACT_APP_BACKEND_URL is not set (for production behind nginx)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

console.log('API configured with base URL:', API_BASE);

// Create axios instance with defaults
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                                   error.config?.url?.includes('/auth/register') ||
                                   error.config?.url?.includes('/auth/logout');
            // Don't clear token on login/register/logout failures
            if (!isAuthEndpoint) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                // Only redirect if not already on auth page and not during active logout
                const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
                if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register') && !isLoggingOut) {
                    window.location.href = '/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    // Two-step registration with email link
    initiateRegistration: (data) => api.post('/auth/register/initiate', data),
    verifyRegistration: (token) => api.get(`/auth/register/verify?token=${token}`),
    resendRegistrationLink: (data) => api.post('/auth/register/resend', data),
    // Legacy register (kept for backward compatibility)
    register: (data) => api.post('/auth/register/initiate', data),
    registerWithInvite: (data) => api.post('/users/register-with-invite', data),
    login: (data) => api.post('/auth/login', data),
    googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    // 2FA endpoints
    get2FAStatus: () => api.get('/auth/2fa/status'),
    enable2FA: () => api.post('/auth/2fa/enable'),
    confirmEnable2FA: (data) => api.post('/auth/2fa/confirm-enable', data),
    disable2FA: (data) => api.post('/auth/2fa/disable', data),
    verify2FA: (data) => api.post('/auth/2fa/verify', data),
    resend2FAOTP: (data) => api.post('/auth/2fa/send-otp', data),
};

// Users APIs
export const usersAPI = {
    getByUsername: (username) => api.get(`/users/profile/${username}`),
    getStats: (userId) => api.get(`/users/${userId}/stats`),
    // Followed sellers
    followSeller: (sellerId) => api.post(`/followed-sellers/${sellerId}`),
    unfollowSeller: (sellerId) => api.delete(`/followed-sellers/${sellerId}`),
    getFollowedSellers: () => api.get('/followed-sellers'),
    checkFollowing: (sellerId) => api.get(`/followed-sellers/${sellerId}/check`),
    getFollowersCount: (userId) => api.get(`/users/${userId}/followers-count`),
    // Saved searches
    getSavedSearches: () => api.get('/saved-searches'),
    createSavedSearch: (data) => api.post('/saved-searches', data),
    deleteSavedSearch: (searchId) => api.delete(`/saved-searches/${searchId}`),
    toggleSavedSearch: (searchId) => api.put(`/saved-searches/${searchId}/toggle`),
};

// Categories APIs
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    getBySlug: (slug) => api.get(`/categories/${slug}`),
};

// Listings APIs
export const listingsAPI = {
    create: (data) => api.post('/listings', data),
    getAll: (params) => api.get('/listings', { params }),
    getFeatured: () => api.get('/listings/featured'),
    getMostViewed: (limit = 10) => api.get('/listings/most-viewed', { params: { limit } }),
    getById: (id, trackView = false) => api.get(`/listings/${id}`, { params: { track_view: trackView } }),
    getBySlug: (slug, trackView = false) => api.get(`/listings/by-slug/${slug}`, { params: { track_view: trackView } }),
    update: (id, data) => api.put(`/listings/${id}`, data),
    delete: (id) => api.delete(`/listings/${id}`),
    getMy: (status) => api.get('/my-listings', { params: { status } }),
    getMyBids: () => api.get('/listings/my-bids'),
    getMyAuctions: (status) => api.get('/my-auctions', { params: { status } }),
    getMyDomains: (params) => api.get('/listings/my-domains', { params }),
    getParticipating: () => api.get('/participating-auctions'),
    searchSuggestions: (q, limit = 6) => api.get('/listings/search/suggestions', { params: { q, limit } }),
    reducePrice: (id, newPrice) => api.post(`/listings/${id}/reduce-price`, { new_price: newPrice }),
    addImages: (id, images) => api.post(`/listings/${id}/images`, { images }),
    deleteImage: (id, imageIndex) => api.delete(`/listings/${id}/images/${imageIndex}`),
    reorderImages: (id, images) => api.put(`/listings/${id}/images/reorder`, { images }),
    // Likes
    toggleLike: (id) => api.post(`/listings/${id}/like`),
    getLikes: (id) => api.get(`/listings/${id}/likes`),
    getLikeStatus: (id) => api.get(`/listings/${id}/like-status`),
    getMyLikes: () => api.get('/my-likes'),
    // Reserve/Unreserve
    toggleReserve: (id) => api.post(`/listings/${id}/reserve`),
};

// Bids APIs
export const bidsAPI = {
    place: (data) => api.post('/bids', data),
    getByListing: (listingId) => api.get(`/bids/${listingId}`),
    getMy: () => api.get('/my-bids'),
};

// Orders APIs
export const ordersAPI = {
    create: (data) => api.post('/orders', data),
    getAll: (role = 'buyer') => api.get('/orders', { params: { role } }),
    getMy: () => api.get('/orders', { params: { role: 'buyer' } }),
    getById: (id) => api.get(`/orders/${id}`),
    pay: (id) => api.post(`/orders/${id}/pay`),
    ship: (id, trackingNumber, courierCompany) => api.post(`/orders/${id}/ship`, { tracking_number: trackingNumber, courier_company: courierCompany }),
    confirmDelivery: (id) => api.post(`/orders/${id}/confirm-delivery`),
    mockPayment: (id) => api.post(`/orders/${id}/mock-payment`),
    createDispute: (id, reason) => api.post(`/orders/${id}/dispute`, { reason }),
    closeDispute: (id) => api.post(`/orders/${id}/close-dispute`),
    // Admin order actions
    adminConfirmPayment: (id) => api.post(`/admin/orders/${id}/confirm-payment`),
    adminConfirmShipped: (id, trackingNumber, courierCompany) => api.post(`/admin/orders/${id}/confirm-shipped`, { tracking_number: trackingNumber, courier_company: courierCompany }),
    adminConfirmReceived: (id) => api.post(`/admin/orders/${id}/confirm-received`),
    adminMarkCompleted: (id) => api.post(`/admin/orders/${id}/complete`),
    adminCancelOrder: (id) => api.post(`/admin/orders/${id}/cancel`),
    adminUpdateStatus: (id, orderStatus, escrowStatus) => api.put(`/admin/orders/${id}/status`, { order_status: orderStatus, escrow_status: escrowStatus }),
};

// Reviews APIs
export const reviewsAPI = {
    create: (data) => api.post('/reviews', data),
    getByUsername: (username) => api.get(`/reviews/user/${username}`),
    getByOrder: (orderId) => api.get(`/reviews/order/${orderId}`),
    replyToReview: (reviewId, content) => api.post(`/reviews/${reviewId}/reply`, { content }),
    deleteReply: (reviewId) => api.delete(`/reviews/${reviewId}/reply`),
};

// Watchlist APIs
export const watchlistAPI = {
    add: (listingId) => api.post('/watchlist', { listing_id: listingId }),
    remove: (listingId) => api.delete(`/watchlist/${listingId}`),
    getMy: () => api.get('/watchlist'),
};

// Reports APIs
export const reportsAPI = {
    create: (data) => api.post('/reports', data),
    createWithImages: async (data, images) => {
        // First upload images if any
        let imageUrls = [];
        if (images && images.length > 0) {
            for (const img of images) {
                const formData = new FormData();
                formData.append('file', img.file);
                try {
                    const res = await api.post('/uploads/images', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    if (res.data?.image_url) {
                        imageUrls.push(res.data.image_url);
                    }
                } catch (error) {
                    console.error('Error uploading report image:', error);
                }
            }
        }
        // Then create report with image URLs
        return api.post('/reports', { ...data, images: imageUrls });
    },
    getAll: () => api.get('/reports'),
    update: (id, status, adminNotes) => api.put(`/reports/${id}`, { status, admin_notes: adminNotes }),
};

// User APIs
export const userAPI = {
    getProfile: (username) => api.get(`/users/${username}`),
    updateMe: (data) => api.put('/users/me', data),
    changePassword: (data) => api.put('/users/me/password', data),
    getInviteLink: () => api.get('/users/me/invite-link'),
    registerWithInvite: (data) => api.post('/users/register-with-invite', data),
    uploadProfilePhoto: (formData) => api.post('/users/me/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

// Uploads APIs
export const uploadsAPI = {
    uploadImage: (formData) => api.post('/uploads/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

// Admin APIs
export const adminAPI = {
    getStats: () => api.get('/admin/stats'),
    getDashboard: () => api.get('/admin/dashboard'),
    getUsers: (params) => api.get('/admin/users', { params }),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    banUser: (id, data) => api.post(`/admin/users/${id}/ban`, data),
    unbanUser: (id) => api.post(`/admin/users/${id}/unban`),
    setUserPassword: (id, newPassword) => api.put(`/admin/users/${id}/password`, { new_password: newPassword }),
    getOrders: (params) => api.get('/admin/orders', { params }),
    blockFunds: (orderId) => api.post(`/admin/orders/${orderId}/block-funds`),
    cancelOrder: (orderId) => api.post(`/admin/orders/${orderId}/cancel`),
    getActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
    getDisputes: (status) => api.get('/admin/disputes', { params: { status } }),
    resolveDispute: (id, data) => api.post(`/admin/disputes/${id}/resolve`, data),
    addWarning: (userId, reason) => api.post(`/admin/users/${userId}/warnings`, { reason }),
    removeWarning: (userId) => api.delete(`/admin/users/${userId}/warnings`),
    sendBroadcast: (content, subject, sendEmail = false) => api.post('/admin/broadcast-message', { content, subject, send_email: sendEmail }),
    getBroadcasts: (params) => api.get('/admin/broadcasts', { params }),
    // Admin notes (private, only visible to admins)
    getUserNotes: (userId) => api.get(`/admin/users/${userId}/notes`),
    updateUserNotes: (userId, notes) => api.put(`/admin/users/${userId}/notes`, { notes }),
    // Admin note for user (creates a report entry)
    addUserAdminNote: (userId, note) => api.post(`/admin/users/${userId}/admin-note`, { note }),
    // Announcements
    getAnnouncements: () => api.get('/admin/announcements'),
    createAnnouncement: (data) => api.post('/admin/announcements', data),
    updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
    deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
    // Maintenance mode
    getMaintenanceStatus: () => api.get('/admin/maintenance'),
    setMaintenanceMode: (enabled, message) => api.post('/admin/maintenance', { enabled, message }),
    // Content management
    restoreListing: (listingId) => api.post(`/admin/content/restore/${listingId}`),
    removeListing: (listingId, reason) => api.post(`/admin/content/remove/${listingId}`, { reason }),
    // Backup & Restore
    getBackupPreview: () => api.get('/admin/backup/preview'),
    downloadBackup: () => api.get('/admin/backup', { responseType: 'blob' }),
    restoreBackup: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/admin/restore', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    // Google OAuth config check
    checkGoogleConfig: () => api.get('/auth/google/config-check'),
    // Visitors tracking
    getVisitors: (skip = 0, limit = 50, ip = null, url = null) => api.get('/admin/visitors', { params: { skip, limit, ip, url } }),
    getVisitorsStats: (days = 7) => api.get('/admin/visitors/stats', { params: { days } }),
};

// Public Announcements API
export const announcementsAPI = {
    getActive: () => api.get('/announcements'),
};

// Payment APIs
export const paymentAPI = {
    createCheckout: (orderId, originUrl) => api.post('/payments/create-checkout', { order_id: orderId, origin_url: originUrl }),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Upload APIs
export const uploadAPI = {
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getImage: (imageId) => api.get(`/images/${imageId}`),
};

// Notification APIs
export const notificationAPI = {
    subscribe: (data) => api.post('/notifications/subscribe', data),
    getEndingSoon: () => api.get('/notifications/ending-soon'),
};

// Notifications API (user notifications)
export const notificationsAPI = {
    getAll: (params) => api.get('/notifications/my', { params }),
    markAllAsRead: () => api.post('/notifications/mark-read'),
    deleteAll: () => api.delete('/notifications/all'),
    delete: (id) => api.delete(`/notifications/${id}`),
    getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Email verification
export const emailAPI = {
    sendVerification: () => api.post('/auth/send-verification-email'),
    verify: (token) => api.post('/auth/verify-email', { token }),
};

// Messaging APIs
export const messagesAPI = {
    send: (data) => api.post('/messages', data),
    sendNew: (data) => api.post('/messages/new', data),
    getConversations: () => api.get('/messages/conversations'),
    getMessages: (conversationId, params) => api.get(`/messages/${conversationId}`, { params }),
    getUnreadCount: () => api.get('/messages/unread/count'),
    getRecent: (skip = 0, limit = 20) => api.get('/messages/recent', { params: { skip, limit } }),
    markRead: (messageId) => api.post(`/messages/${messageId}/read`),
    markConversationRead: (conversationId) => api.post(`/messages/${conversationId}/read-all`),
    sendTyping: (conversationId, isTyping = true) => api.post(`/messages/typing/${conversationId}`, { is_typing: isTyping }),
    getTypingStatus: (conversationId) => api.get(`/messages/typing/${conversationId}`),
};

// Site Settings APIs
export const settingsAPI = {
    get: () => api.get('/settings'),
    update: (data) => api.put('/settings', data),
    getContactAdmin: () => api.get('/settings/contact-admin'),
    setContactAdmin: (data) => api.put('/settings/contact-admin', data),
    getAdminsList: () => api.get('/settings/admins-list'),
};

export default api;
